/**
 * RAG Service for AI Lawyer Frontend
 * Provides legal knowledge queries and contract enhancement with Czech legal context
 */

import { api } from './api';

export interface LegalQuery {
  query: string;
  mode?: 'naive' | 'local' | 'global' | 'hybrid';
  max_results?: number;
}

export interface LegalQueryResponse {
  success: boolean;
  query: string;
  response: string;
  mode: string;
  metadata: {
    available: boolean;
    timestamp: string;
    working_dir: string;
  };
  error?: string;
}

export interface ContractEnhancement {
  contract_type: 'purchase' | 'rental' | 'employment';
  contract_data: Record<string, any>;
}

export interface ContractEnhancementResponse {
  success: boolean;
  original_data: Record<string, any>;
  legal_enhancements: Array<{
    query: string;
    legal_context: string;
    relevance: string;
  }>;
  recommendations: string[];
  enhanced: boolean;
  error?: string;
}

export interface RAGStatus {
  initialized: boolean;
  lightrag_available: boolean;
  working_dir: string;
  legal_categories: string[];
  embedding_model_dimension: number | null;
}

export interface AddLegalDocument {
  title: string;
  content: string;
  category?: string;
}

class RAGService {
  private baseURL = '/rag';

  /**
   * Get RAG service status and configuration
   */
  async getStatus(): Promise<RAGStatus> {
    const response = await api.get(`${this.baseURL}/status`);
    return response.data;
  }

  /**
   * Query legal knowledge base for relevant information
   */
  async queryLegalKnowledge(queryData: LegalQuery): Promise<LegalQueryResponse> {
    const response = await api.post(`${this.baseURL}/query`, {
      query: queryData.query,
      mode: queryData.mode || 'hybrid',
      max_results: queryData.max_results || 5
    });
    return response.data;
  }

  /**
   * Enhance contract with legal context and recommendations
   */
  async enhanceContract(enhancementData: ContractEnhancement): Promise<ContractEnhancementResponse> {
    const response = await api.post(`${this.baseURL}/enhance-contract`, enhancementData);
    return response.data;
  }

  /**
   * Add legal document to knowledge base
   */
  async addLegalDocument(documentData: AddLegalDocument): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`${this.baseURL}/add-document`, documentData);
    return response.data;
  }

  /**
   * Get available legal categories
   */
  async getLegalCategories(): Promise<{ categories: Record<string, string>; total_categories: number }> {
    const response = await api.get(`${this.baseURL}/categories`);
    return response.data;
  }

  /**
   * Get sample legal queries for different contract types
   */
  async getSampleQueries(): Promise<{
    purchase_contract_queries: string[];
    rental_contract_queries: string[];
    employment_contract_queries: string[];
    general_legal_queries: string[];
  }> {
    const response = await api.get(`${this.baseURL}/sample-queries`);
    return response.data;
  }

  /**
   * Utility method to get contract-specific legal recommendations
   */
  async getContractRecommendations(
    contractType: 'purchase' | 'rental' | 'employment',
    contractData: Record<string, any>
  ): Promise<string[]> {
    try {
      const enhancement = await this.enhanceContract({
        contract_type: contractType,
        contract_data: contractData
      });

      if (enhancement.success && enhancement.enhanced) {
        return enhancement.recommendations;
      }

      return [];
    } catch (error) {
      console.error('Failed to get contract recommendations:', error);
      return [];
    }
  }

  /**
   * Utility method to get price-based recommendations
   */
  getStaticRecommendations(contractType: string, data: Record<string, any>): string[] {
    const recommendations: string[] = [];

    switch (contractType) {
      case 'purchase':
        const price = Number(data.price || 0);
        
        // Always add basic recommendations
        recommendations.push('Ověřte si identitu prodávajícího a jeho vlastnické právo k věci');
        recommendations.push('Dohodněte si způsob předání věci a kontrolu jejího stavu');
        
        if (price > 50000) {
          const isAnimals = (data.contract_type === 'zvirata');
          const idText = isAnimals ? 'evidenčního čísla (čipu)' : 'sériového čísla';
          recommendations.push(`Pro kupní cenu nad 50.000 Kč doporučujeme sjednání písemné formy a detailní specifikace věci včetně ${idText}`);
          recommendations.push('Zvažte sjednání zálohy a splátkový kalendář pro vysokou částku');
        }
        if (price > 100000) {
          recommendations.push('Pro vysokou kupní cenu zvažte sjednání pojištění a notářské ověření podpisů');
          recommendations.push('Doporučujeme právní kontrolu smlouvy před podpisem');
        }
        break;

      case 'rental':
        const rent = Number(data.rent_amount || data.monthlyRent || 0);
        
        // Always add basic recommendations
        recommendations.push('Důkladně si prohlédněte nemovitost před podpisem smlouvy');
        recommendations.push('Dohodněte si výši kauce a způsob jejího vrácení');
        
        if (rent > 20000) {
          recommendations.push('Pro nájemné nad 20.000 Kč doporučujeme detailní úpravu kauce (max. 6x měsíční nájemné) a pojištění odpovědnosti');
          recommendations.push('Sjednejte si podrobný předávací protokol s fotodokumentací');
        }
        if (rent > 40000) {
          recommendations.push('Pro vysoké nájemné zvažte profesionální správu nemovitosti a právní kontrolu smlouvy');
        }
        break;

      case 'employment':
        const salary = Number(data.salary || data.monthlySalary || 0);
        
        // Always add basic recommendations
        recommendations.push('Ujistěte se, že smlouva obsahuje všechny povinné náležitosti podle zákoníku práce');
        recommendations.push('Ověřte si délku zkušební doby (max. 3 měsíce) a podmínky pro ukončení');
        
        if (salary > 80000) {
          recommendations.push('Pro mzdu nad 80.000 Kč doporučujeme sjednání konkurenční doložky a detailní vymezení pracovních povinností');
          recommendations.push('Zvažte sjednání dodatečných benefitů (služební auto, telefon, stravenky)');
        }
        if (salary > 150000) {
          recommendations.push('Pro vysokou mzdu zvažte bonusové schéma, stock options a podrobnou úpravu intelektuálního vlastnictví');
        }
        break;
    }

    return recommendations;
  }

  /**
   * Check if RAG service is available
   */
  async isAvailable(): Promise<boolean> {
    try {
      const status = await this.getStatus();
      return status.lightrag_available;
    } catch (error) {
      console.warn('RAG service not available:', error);
      return false;
    }
  }
}

// Export singleton instance
export const ragService = new RAGService();
export default ragService;
